import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../../api/axiosInstance';
import './TagAutocomplete.css';

interface TagAutocompleteProps {
  selectedTags: string[];
  onChange: (tags: string[]) => void;
}

const TagAutocomplete: React.FC<TagAutocompleteProps> = ({ selectedTags, onChange }) => {
  const [allTags, setAllTags] = useState<{ id: number; name: string }[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<{ id: number; name: string }[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 모든 태그 로드
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const res = await axiosInstance.get('/api/tags');
        setAllTags(res.data);
      } catch (err) {
        console.error('태그 로드 실패:', err);
      }
    };
    fetchTags();
  }, []);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 추천 태그 필터링
  useEffect(() => {
    if (!inputValue.trim()) {
      setSuggestions([]);
      return;
    }
    const filtered = allTags.filter(
      tag =>
        tag.name.toLowerCase().includes(inputValue.toLowerCase()) &&
        !selectedTags.includes(tag.name)
    );
    setSuggestions(filtered);
    setActiveIndex(-1);
  }, [inputValue, allTags, selectedTags]);

  const handleAddTag = (tagName: string) => {
    const trimmed = tagName.trim();
    if (trimmed && !selectedTags.includes(trimmed)) {
      onChange([...selectedTags, trimmed]);
    }
    setInputValue('');
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (isOpen && activeIndex >= 0 && suggestions[activeIndex]) {
        handleAddTag(suggestions[activeIndex].name);
      } else {
        handleAddTag(inputValue);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen && suggestions.length > 0) {
        setIsOpen(true);
      }
      setActiveIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleRemoveTag = (indexToRemove: number) => {
    onChange(selectedTags.filter((_, i) => i !== indexToRemove));
  };

  return (
    <div className="tag-autocomplete-container" ref={dropdownRef}>
      <div className="tag-input-wrapper">
        <input
          type="text"
          value={inputValue}
          onChange={e => {
            setInputValue(e.target.value);
            setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsOpen(true)}
          placeholder="태그를 입력하고 엔터 또는 쉼표를 누르세요"
          className="tag-text-input"
        />
        {isOpen && suggestions.length > 0 && (
          <ul className="suggestions-list">
            {suggestions.map((tag, index) => (
              <li
                key={tag.id}
                onClick={() => handleAddTag(tag.name)}
                className={`suggestion-item ${index === activeIndex ? 'active' : ''}`}
                onMouseEnter={() => setActiveIndex(index)}
              >
                🏷️ {tag.name}
              </li>
            ))}
          </ul>
        )}
      </div>

      {selectedTags.length > 0 && (
        <div className="selected-tags-container">
          {selectedTags.map((tag, index) => (
            <span key={index} className="selected-tag-chip">
              🏷️ {tag}
              <button
                type="button"
                onClick={() => handleRemoveTag(index)}
                className="remove-tag-btn"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default TagAutocomplete;
